import React from 'react';

/**
 * next-seo ships one component per schema type, each covering a fixed set of fields, and none of
 * them can emit `@id` cross references. Those references are what tie a page to the site and the
 * site to the company, so that search and answer engines resolve one entity graph instead of
 * several unrelated nodes. Anything needing that linkage is rendered through this component.
 */
type JsonLdProps = {
  // Distinguishes the nodes rendered alongside each other; used as the React key.
  id: string;
  data: Record<string, unknown>;
};

/**
 * A "</script>" sequence inside any string value would close the tag early, so the characters that
 * could start one are escaped. Page titles and descriptions reach this component from Strapi, so
 * the payload is not entirely under our control.
 */
const serialiseJsonLd = (data: Record<string, unknown>) =>
  JSON.stringify({ '@context': 'https://schema.org', ...data })
    .replace(/</g, '\u003c')
    .replace(/>/g, '\u003e')
    .replace(/&/g, '\u0026');

const JsonLd: React.FC<JsonLdProps> = ({ id, data }) => (
  <script key={`json-ld-${id}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: serialiseJsonLd(data) }} />
);
JsonLd.displayName = 'JsonLd';
export default JsonLd;
